/**
 * ObjektListe — the versicherte Objekte of a policy (P2 Placement Slice 2, Ch 2.2 insurance_object_ref).
 * The NULL-ref entry is always present as «Police allgemein»; the broker can add per-object sets
 * (a vehicle in a fleet, a building, …) and soft-remove them. Each object renders an
 * IstAttributeSection for its own Ist-Attribute set.
 *
 * Behind the `policen.risiko_deckung.objekte` Kachel visibility key.
 */

import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import type {
  BranchenFeldDefinition,
  GesundheitspruefungStatus,
  PoliceRisikoObjekt,
  PoliceRisikoPatchBody,
} from '@brokly/shared';
import { Button } from '../../../../components/ui/Button';
import { IstAttributeSection } from './IstAttributeSection';

export function ObjektListe({
  felder,
  objekte,
  canWrite,
  isSaving,
  onPatch,
  onRemove,
  gesundheitspruefungStatus,
  gatedSuppressed,
}: {
  felder: BranchenFeldDefinition[];
  objekte: PoliceRisikoObjekt[];
  canWrite: boolean;
  isSaving: boolean;
  onPatch: (body: PoliceRisikoPatchBody) => void;
  /** Soft-remove one object by its row id (the DELETE-object route key). */
  onRemove: (objectId: string) => void;
  gesundheitspruefungStatus?: GesundheitspruefungStatus | null;
  /** The policy-level mirror is rendered by IstAttributeSection; per-object sets carry none. */
  gatedSuppressed?: string[];
}) {
  const [newRef, setNewRef] = useState('');
  const [newLabel, setNewLabel] = useState('');

  function addObject() {
    const ref = newRef.trim();
    if (ref === '') return;
    onPatch({ insurance_object_ref: ref, object_label: newLabel.trim() === '' ? null : newLabel.trim(), attributes: {} });
    setNewRef('');
    setNewLabel('');
  }

  return (
    <section aria-labelledby="pr-objekte" className="flex flex-col gap-4" data-testid="pr-objekte">
      <h3 id="pr-objekte" className="text-sm font-semibold text-foreground">
        Ist-Attribute je Objekt
      </h3>

      {objekte.map((objekt) => (
        <div key={objekt.insurance_object_ref ?? 'policy'} className="relative">
          {canWrite && objekt.insurance_object_ref !== null && (
            <button
              type="button"
              aria-label={`Objekt ${objekt.object_label ?? objekt.insurance_object_ref} entfernen`}
              onClick={() => onRemove(objekt.id)}
              disabled={isSaving}
              className="absolute right-3 top-3 z-10 grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-destructive"
              data-testid={`pr-objekt-remove-${objekt.insurance_object_ref}`}
            >
              <Trash2 className="size-3.5" />
            </button>
          )}
          <IstAttributeSection
            felder={felder}
            objekt={objekt}
            canWrite={canWrite}
            isSaving={isSaving}
            onPatch={onPatch}
            gesundheitspruefungStatus={objekt.insurance_object_ref === null ? gesundheitspruefungStatus : undefined}
            gatedSuppressed={objekt.insurance_object_ref === null ? (gatedSuppressed ?? []) : []}
          />
        </div>
      ))}

      {canWrite && (
        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-dashed bg-card p-3" data-testid="pr-objekt-add">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Objekt-Referenz</span>
            <input
              value={newRef}
              disabled={isSaving}
              onChange={(e) => setNewRef(e.target.value)}
              placeholder="z. B. Fahrzeug 1"
              aria-label="Objekt-Referenz"
              className="rounded-md border bg-background px-2 py-1 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Bezeichnung (optional)</span>
            <input
              value={newLabel}
              disabled={isSaving}
              onChange={(e) => setNewLabel(e.target.value)}
              aria-label="Objekt-Bezeichnung"
              className="rounded-md border bg-background px-2 py-1 text-sm"
            />
          </label>
          <Button size="sm" variant="outline" onClick={addObject} disabled={isSaving || newRef.trim() === ''}>
            Objekt hinzufuegen
          </Button>
        </div>
      )}
    </section>
  );
}
